import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TuiValidationError } from '@taiga-ui/cdk';
import { TranslateService } from '@ngx-translate/core';
import { Nullable } from '@src/types/utils';
import { BaseResizableComponent } from '@src/app/modules/base';
import { AlertService } from '@src/core/services';
import { requireContactsValidator } from '@src/app/modules/contacts';
import { isFileContact } from '@src/utils';

import { PermissionService } from '../../services';
import { ContactType, PageInfoMode, PageInfoType } from '../../types';

import { FormDataControlsType, FormDataType } from './types';

@Component({
  selector: 'info-edit',
  templateUrl: './info-edit.component.html',
  styleUrls: ['./info-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoEditComponent extends BaseResizableComponent implements OnChanges {
  private readonly permService = inject(PermissionService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);

  @Input() mode?: PageInfoMode;
  @Input() data?: PageInfoType | null;
  @Input() loading = false;
  @Output() saved = new EventEmitter<PageInfoType>();
  @Output() canceled = new EventEmitter<void>();

  form!: FormGroup<FormDataControlsType>;

  readonly allowEditing$ = this.permService.allowEditing$;

  constructor() {
    super();

    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.form.reset();

      if (this.data) {
        const formData = this.mapPageInfoTypeToFormData(this.data);
        this.form.patchValue(formData);
      }
    }

    this.cdr.markForCheck();
  }

  onClickSaveButton() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fillRequired'));
      return;
    }

    const apiData = this.mapFormDataToPageInfoType(this.form.value);
    this.saved.emit(apiData);
    this.cdr.markForCheck();
  }

  onClickCancelButton() {
    this.canceled.emit();
  }

  controlValueError(key: string): Nullable<TuiValidationError> {
    const control = this.form.get(key);
    if (!control) {
      return null;
    }

    if (control.touched && control.errors?.maxlength) {
      return control.errors.maxlength.requiredLength;
    }

    return null;
  }

  private initForm(): void {
    this.form = new FormGroup<FormDataControlsType>({
      id: new FormControl(null),
      title: new FormControl(null, [Validators.required, Validators.maxLength(1000)]),
      contentText: new FormControl(null, [Validators.required, Validators.maxLength(20000)]),
      showInMenu: new FormControl(false, { nonNullable: true }),
      sortOrder: new FormControl(null, [Validators.required]),
      contacts: new FormControl<ContactType[]>([], {
        nonNullable: true,
        validators: requireContactsValidator(),
      }),
    });
  }

  private mapPageInfoTypeToFormData(pageInfo: PageInfoType): FormDataType {
    const { id, title, contentText, showInMenu, sortOrder, contacts } = pageInfo;

    contacts?.map((contact: ContactType) => {
      if (isFileContact(contact.contactTypeId) && contact.contact) {
        contact.oldDocument = {
          id: contact.contact,
          name: contact.description ?? this.translateService.instant('common.labels.file'),
        };
      }

      contact.description = contact.description?.trim();
      return contact;
    });

    return <FormDataType>{
      id,
      title,
      contentText,
      showInMenu,
      sortOrder,
      contacts: contacts ?? [],
    };
  }

  private mapFormDataToPageInfoType(formData: FormDataType): PageInfoType {
    const { id, title, contentText, showInMenu, sortOrder } = formData;
    const contacts = formData.contacts?.map((contact: ContactType) => {
      if (!contact.id) {
        delete contact.id;
      }

      delete contact.contactListValues;

      contact.description = contact.description?.trim(); // TODO: перенести в Interceptor #11548
      return contact;
    });

    return <PageInfoType>{
      id,
      title,
      contentText,
      showInMenu,
      sortOrder,
      contacts: contacts ?? [],
    };
  }
}
